/**
 * frontend/src/pages/valuation/_useTuningInputs.ts
 *
 * VAL-TUNING-SPLIT-1 — the input/hydration half of ValuationTuning.tsx,
 * split out under docs/FILE_SIZE_POLICY.md (the screen file crossed the
 * 400-line hard-stop threshold). Owns every input state the screen reads
 * and writes — driver answers, tuning knobs, rationale, the
 * market-only acknowledgement — the active-config fetch, and the two
 * hydration source for them (the saved draft and its saved tuning — nothing
 * held in the browser). No new logic, no renamed fields: this is the same
 * code that lived on the screen, moved verbatim.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import { fetchActiveConfig } from '@/api/valuationB3'
import type { ConfigResponse, TuningBody, TuningKnobs } from '@/types/valuationB3'
import type { DraftInputs } from '@/types/valuationDraft'
import { adaptQuestionnaire, baselineKnobs } from './_valuationTuningHelpers'

export function useTuningInputs(
  id: string | undefined,
  draft: DraftInputs | null,
  dismissDraft: () => void,
  setLoadError: (message: string) => void,
  // VAL-INPUT-PERSIST-1E — the tuning saved on the draft (own column, not in
  // `draft`). The saved draft is the only source.
  draftTuning: TuningBody | null = null,
) {
  const [answers, setAnswersRaw] = useState<Record<string, string>>({})
  const [knobs, setKnobsRaw] = useState<Record<string, number>>({})
  const [rationale, setRationaleRaw] = useState('')
  const hydratedRef = useRef(false)
  // VAL-PATH-SIGNER P7b — earnings are no longer typed here: Methodology and
  // rates shows the applied figure and the signer's override (_useMethodologyEdits).

  // VAL-DRAFT-OVERWRITE-1 — what the user has touched. A late draft hydrate or
  // config load fills only what they have not; it never replaces their edit.
  const touched = useRef({
    ack: false,
    rationale: false,
    answers: new Set<string>(),
    knobs: new Set<string>(),
  })

  const setRationale = useCallback((v: string) => {
    touched.current.rationale = true
    setRationaleRaw(v)
  }, [])
  const setAnswers = useCallback(
    (next: SetStateAction<Record<string, string>>) =>
      setAnswersRaw(prev => {
        const n = typeof next === 'function' ? next(prev) : next
        for (const k of Object.keys(n)) if (n[k] !== prev[k]) touched.current.answers.add(k)
        return n
      }),
    [],
  )

  // D6 — a market-only calculated value must be an explicit, recorded
  // acknowledgement, not a note buried in the warnings list.
  const [acknowledgeMarketOnly, setAckRaw] = useState(false)
  const setAcknowledgeMarketOnly = useCallback((v: boolean) => {
    touched.current.ack = true
    setAckRaw(v)
  }, [])
  /** The screen's own reset (not a user edit) when the value is not market-only. */
  const clearMarketOnlyAck = useCallback(() => setAckRaw(false), [])

  useEffect(() => {
    if (hydratedRef.current || !draft) return
    hydratedRef.current = true
    const t = touched.current
    setAnswersRaw(prev => {
      const merged = { ...(draft.answers ?? {}) }
      for (const k of t.answers) if (k in prev) merged[k] = prev[k]
      return merged
    })
    // D6 — resume a prior acknowledgement too, not just the numbers. Race-safe
    // against the `isMarketOnly` reset effect below: that effect is a no-op
    // whenever `isMarketOnly` is true, so a hydrated `true` survives it; when
    // `isMarketOnly` is false both effects agree on `false`.
    if (!t.ack) setAckRaw(draft.market_only_acknowledged ?? false)
  }, [draft, id])

  /** D3 "start clean" — clears the resumed form back to blank; the draft row
   * itself is untouched (Intake's half of it must survive this). */
  function startClean() {
    dismissDraft()
    touched.current.answers.clear()
    setAnswersRaw({})
  }

  /** Knobs the user moved, kept over a freshly computed set. */
  function keepMovedKnobs(fresh: Record<string, number>, prev: Record<string, number>) {
    const out = { ...fresh }
    for (const k of touched.current.knobs) if (k in prev) out[k] = prev[k]
    return out
  }

  /** "Reset to baseline" — an explicit replace, so nothing stays pinned. */
  function resetKnobs(to: Record<string, number>) {
    touched.current.knobs.clear()
    setKnobsRaw(to)
  }

  const [config, setConfig] = useState<ConfigResponse | null>(null)
  // Reload key, not just a bare effect — R4 4d needs to refetch the active
  // config after an activation from ConfigVersionHistory changes which
  // version this screen (and the questionnaire/knobs baseline) is on.
  const [configReloadKey, setConfigReloadKey] = useState(0)
  useEffect(() => {
    let live = true
    fetchActiveConfig()
      .then(res => {
        if (!live) return
        setConfig(res)
        setKnobsRaw(prev => keepMovedKnobs(baselineKnobs(res), prev))
      })
      .catch(() => live && setLoadError('Could not load the active methodology.'))
    return () => {
      live = false
    }
  }, [configReloadKey])

  const questionnaire = useMemo(() => (config ? adaptQuestionnaire(config) : null), [config])
  const baseline = useMemo(() => (config ? baselineKnobs(config) : {}), [config])

  const draftTuningHydratedRef = useRef(false)
  useEffect(() => {
    if (draftTuningHydratedRef.current || !id || !draftTuning) return
    if (Object.keys(baseline).length === 0) return
    draftTuningHydratedRef.current = true
    const { rationale: savedRationale, version: _v, ...overrides } = draftTuning
    setKnobsRaw(prev => keepMovedKnobs({ ...baseline, ...overrides }, prev))
    if (!touched.current.rationale) setRationaleRaw(savedRationale ?? '')
  }, [id, baseline, draftTuning])

  const tuned = useMemo(
    () =>
      Object.keys(baseline).length > 0 &&
      Object.entries(baseline).some(([k, v]) => Math.abs((knobs[k] ?? v) - v) > 1e-9),
    [baseline, knobs],
  )

  function onKnob(field: keyof TuningKnobs, value: number) {
    touched.current.knobs.add(field)
    if (field === 'income_weight') touched.current.knobs.add('market_weight')
    setKnobsRaw(prev =>
      field === 'income_weight'
        ? { ...prev, income_weight: value, market_weight: Number((1 - value).toFixed(4)) }
        : { ...prev, [field]: value },
    )
  }

  return {
    answers,
    setAnswers,
    knobs,
    resetKnobs,
    rationale,
    setRationale,
    acknowledgeMarketOnly,
    setAcknowledgeMarketOnly,
    clearMarketOnlyAck,
    config,
    setConfigReloadKey,
    questionnaire,
    baseline,
    tuned,
    startClean,
    onKnob,
  }
}
